import React, { useState } from 'react';
import {
  ArrowPathIcon,
  ServerIcon,
  DocumentArrowDownIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeSlashIcon,
  UserMinusIcon
} from '@heroicons/react/24/outline';
import SalaryDataMigration from './components/SalaryDataMigration';
import AnonymizeSalaryData from './components/AnonymizeSalaryData';
import DeleteAuthUsersMigration from './components/DeleteAuthUsersMigration';

interface MigrationTool {
  id: string;
  name: string;
  description: string;
  status: 'available' | 'completed' | 'in-progress' | 'error';
  component: React.ComponentType;
  lastRun?: string;
  icon: React.ComponentType<any>;
}

const MigrationTools: React.FC = () => {
  const [selectedTool, setSelectedTool] = useState<string | null>(null);

  // Check if we're in development environment
  const isDevelopment = import.meta.env.DEV ||
    window.location.hostname === 'localhost' ||
    import.meta.env.VITE_REACT_FIREBASE_PROJECT_ID?.includes('dev');

  const migrations: MigrationTool[] = [
    {
      id: 'salary-data',
      name: 'Migração de Dados Salariais',
      description: 'Migra dados de salário para subcoleções com controle de acesso baseado em papéis',
      status: 'available',
      component: SalaryDataMigration,
      icon: DocumentArrowDownIcon,
      lastRun: undefined
    },
    // Destructive Actions
    {
      id: 'delete-auth-users',
      name: 'Apagar Todos os Usuários de Autenticação',
      description: 'CUIDADO: Remove permanentemente todos os usuários do Firebase Authentication. Eles perderão acesso até fazerem login novamente.',
      status: 'available',
      component: DeleteAuthUsersMigration,
      icon: UserMinusIcon,
      lastRun: undefined
    },
    // Anonymization - Only show in development
    ...(isDevelopment ? [{
      id: 'anonymize-salary',
      name: 'Anonimização de Dados Salariais',
      description: `Substitui todos os valores salariais por um valor fixo (padrão: R$ 1.000,00) - APENAS DESENVOLVIMENTO (Projeto atual: ${import.meta.env.VITE_REACT_FIREBASE_PROJECT_ID})`,
      status: 'available' as const,
      component: AnonymizeSalaryData,
      icon: EyeSlashIcon,
      lastRun: undefined
    }] : []),
    // Future migrations can be added here
    // {
    //   id: 'example-migration',
    //   name: 'Example Migration',
    //   description: 'Description of what this migration does',
    //   status: 'completed',
    //   component: ExampleMigration,
    //   icon: ServerIcon,
    //   lastRun: '2024-10-13T10:30:00Z'
    // }
  ];

  const selectedMigration = migrations.find(m => m.id === selectedTool);
  const SelectedComponent = selectedMigration?.component;

  const getStatusBadge = (status: MigrationTool['status']) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="w-4 h-4 mr-1" />
            Concluído
          </span>
        );
      case 'in-progress':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <ArrowPathIcon className="w-4 h-4 mr-1 animate-spin" />
            Em Progresso
          </span>
        );
      case 'error':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircleIcon className="w-4 h-4 mr-1" />
            Erro
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <ServerIcon className="w-4 h-4 mr-1" />
            Disponível
          </span>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <ArrowPathIcon className="h-8 w-8 text-gray-400 mr-3" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Ferramentas de Migração
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  Gerencie migrações de dados e atualizações do sistema
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Warning Banner */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <strong>Atenção:</strong> As migrações podem modificar dados permanentemente.
                Sempre execute um dry-run primeiro e faça backup dos dados antes de prosseguir.
              </p>
            </div>
          </div>
        </div>

        {/* Migration List or Selected Tool */}
        {!selectedTool ? (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">
                Migrações Disponíveis
              </h2>
            </div>
            <ul className="divide-y divide-gray-200">
              {migrations.map((migration) => (
                <li key={migration.id}>
                  <button
                    onClick={() => setSelectedTool(migration.id)}
                    className="block w-full px-6 py-4 hover:bg-gray-50 text-left transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <migration.icon className="h-10 w-10 text-gray-400 mr-4" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {migration.name}
                          </p>
                          <p className="text-sm text-gray-500 mt-1">
                            {migration.description}
                          </p>
                          {migration.lastRun && (
                            <p className="text-xs text-gray-400 mt-1">
                              Última execução: {new Date(migration.lastRun).toLocaleString('pt-BR')}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center">
                        {getStatusBadge(migration.status)}
                        <svg
                          className="ml-4 h-5 w-5 text-gray-400"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-900">
                  {selectedMigration?.name}
                </h2>
                <button
                  onClick={() => setSelectedTool(null)}
                  className="text-sm text-blue-600 hover:text-blue-500"
                >
                  ← Voltar para lista
                </button>
              </div>
            </div>
            <div className="p-6">
              {SelectedComponent && <SelectedComponent />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MigrationTools;